/**
 * Blinkit-theme store row: photo, name, what it sells, delivery time, distance, rating and open state. Used by the
 * stores list and the Shops tab of search. The ETA follows the tenant's own display rule (lib/deliveryEta) — a
 * tenant-set text wins, a hidden ETA stays hidden.
 */
import { Link } from "react-router-dom";
import { Clock, MapPin, Star, Store } from "lucide-react";
import { useTenant } from "../../contexts/TenantContext";
import { computeEtaMinutes, formatDeliveryEta } from "../../lib/deliveryEta";

const CLOSED = new Set(["CLOSED", "PAUSED"]);

export default function BkStoreCard({ r, testid }) {
    const { tenant } = useTenant();
    const img = r.cover_image_url || r.logo_url;
    const state = r.open_state || (r.is_open === false ? "CLOSED" : "OPEN");
    const closed = CLOSED.has(state);
    const eta = formatDeliveryEta(tenant, computeEtaMinutes(r.avg_prep_minutes || 25, r.distance_km));
    const sells = (r.cuisines || []).slice(0, 3).join(", ");
    return (
        <Link to={`/restaurant/${r.id}`} data-testid={testid || `bk-store-${r.id}`}
              className={`bg-card rounded-2xl border border-border/80 p-3 flex items-center gap-3 min-w-0 active:scale-[0.99] transition ${closed ? "opacity-75" : ""}`}>
            <span className={`relative w-[72px] h-[72px] shrink-0 rounded-xl bg-muted overflow-hidden flex items-center justify-center ${closed ? "grayscale" : ""}`}>
                {img ? <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" /> : <Store size={24} className="text-muted-foreground" />}
                {r.is_featured && !closed && (
                    <span className="absolute top-0 inset-x-0 bg-primary text-primary-foreground text-[9px] font-bold text-center py-0.5">FEATURED</span>
                )}
            </span>
            <span className="min-w-0 flex-1">
                <span className="block text-[15px] font-extrabold leading-tight truncate">{r.name}</span>
                {sells && <span className="block text-[12px] text-muted-foreground truncate mt-0.5">{sells}</span>}
                <span className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] font-semibold">
                    {eta && !closed && <span className="inline-flex items-center gap-1"><Clock size={12} className="text-primary" /> {eta}</span>}
                    {r.distance_km != null && <span className="inline-flex items-center gap-1 text-muted-foreground"><MapPin size={12} /> {Number(r.distance_km).toFixed(1)} km</span>}
                    {Number(r.rating) > 0 && <span className="inline-flex items-center gap-1 text-muted-foreground"><Star size={12} className="fill-current text-amber-500" /> {Number(r.rating).toFixed(1)}</span>}
                    {closed && <span className="text-destructive font-bold uppercase text-[11px] tracking-wide">{r.status_label || (state === "PAUSED" ? "Paused" : "Closed")}</span>}
                    {state === "CLOSING_SOON" && <span className="text-amber-700 font-bold text-[11px]">{r.status_label || "Closing soon"}</span>}
                </span>
            </span>
        </Link>
    );
}
